import { useEffect, useRef } from 'react';

interface PreviewImageProps {
    seed?: string;
}

export default function PreviewImage({ seed }: PreviewImageProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Clear canvas with light background
        ctx.fillStyle = '#f8fafc';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Use seed to generate consistent random numbers
        const seededRandom = (seed?: string) => {
            const str = seed || Math.random().toString();
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
                hash = ((hash << 5) - hash) + str.charCodeAt(i);
                hash = hash & hash;
            }
            return Math.abs((Math.sin(hash) + 1) / 2);
        };

        // Add subtle noise pattern
        ctx.globalAlpha = 0.05;
        for (let i = 0; i < canvas.width; i += 2) {
            for (let j = 0; j < canvas.height; j += 2) {
                if (seededRandom((seed || '') + i + j) > 0.5) {
                    ctx.fillStyle = '#94a3b8';
                    ctx.fillRect(i, j, 1, 1);
                }
            }
        }

        // Add subtle grid pattern
        ctx.globalAlpha = 0.08;
        // Vertical lines
        for (let i = 0; i < canvas.width; i += 15) {
            ctx.beginPath();
            ctx.moveTo(i, 0);
            ctx.lineTo(i, canvas.height);
            ctx.strokeStyle = '#94a3b8';
            ctx.lineWidth = 1.5;
            ctx.stroke();
        }
        // Horizontal lines
        for (let i = 0; i < canvas.height; i += 15) {
            ctx.beginPath();
            ctx.moveTo(0, i);
            ctx.lineTo(canvas.width, i);
            ctx.strokeStyle = '#94a3b8';
            ctx.lineWidth = 1.5;
            ctx.stroke();
        }

        // Generate random data points with more variation
        const generatePoints = (offset: number, complexity: number) => {
            return Array.from({ length: 50 }, (_, i) => {
                const x = (i / 49) * canvas.width;
                const baseNoise = seededRandom((seed || '') + i + offset.toString()) * 80;
                const secondaryNoise = Math.sin(i * 0.5) * 30;
                const tertiaryNoise = Math.cos(i * 0.3) * 20;
                const y = (Math.sin(i * 0.3 + offset) * 40) + 
                         (canvas.height * 0.5) + 
                         baseNoise + 
                         secondaryNoise + 
                         tertiaryNoise * complexity;
                return { x, y };
            });
        };

        // Generate multiple curves with different colors
        const colors = [
            { stroke: '#818cf8', fill: 'rgba(129, 140, 248, 0.1)' }, // indigo-400
            { stroke: '#38bdf8', fill: 'rgba(56, 189, 248, 0.1)' },  // sky-400
            { stroke: '#2dd4bf', fill: 'rgba(45, 212, 191, 0.1)' }   // teal-400
        ];

        // Draw curves with varying complexity
        colors.forEach((color, index) => {
            const points = generatePoints(index, 1 + index * 0.5);
            
            // Fill area under curve with gradient
            const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
            gradient.addColorStop(0, color.fill);
            gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
            
            ctx.beginPath();
            ctx.moveTo(0, canvas.height);
            
            points.forEach((point, i) => {
                if (i === 0) {
                    ctx.lineTo(point.x, point.y);
                } else {
                    const xc = (point.x + points[i - 1].x) / 2;
                    const yc = (point.y + points[i - 1].y) / 2;
                    ctx.quadraticCurveTo(points[i - 1].x, points[i - 1].y, xc, yc);
                }
            });
            
            ctx.lineTo(canvas.width, canvas.height);
            ctx.closePath();
            ctx.fillStyle = gradient;
            ctx.fill();

            // Draw the curve with varying line width
            ctx.beginPath();
            points.forEach((point, i) => {
                if (i === 0) {
                    ctx.moveTo(point.x, point.y);
                } else {
                    const xc = (point.x + points[i - 1].x) / 2;
                    const yc = (point.y + points[i - 1].y) / 2;
                    ctx.quadraticCurveTo(points[i - 1].x, points[i - 1].y, xc, yc);
                }
            });
            ctx.strokeStyle = color.stroke;
            ctx.lineWidth = 2 + Math.sin(index) * 0.8;
            ctx.stroke();

            // Add dots at peaks and valleys with varying sizes
            points.forEach((point, i) => {
                if (i > 0 && i < points.length - 1) {
                    const prev = points[i - 1].y;
                    const curr = point.y;
                    const next = points[i + 1].y;
                    
                    if ((curr < prev && curr < next) || (curr > prev && curr > next)) {
                        const size = 1.5 + seededRandom((seed || '') + i) * 2;
                        ctx.beginPath();
                        ctx.arc(point.x, point.y, size, 0, Math.PI * 2);
                        ctx.fillStyle = color.stroke;
                        ctx.fill();
                    }
                }
            });
        });

    }, [seed]);

    return (
        <canvas 
            ref={canvasRef} 
            width={500} 
            height={200} 
            className="w-full h-full pointer-events-none outline-none border-none"
            style={{ outline: 'none' }}
        />
    );
} 